import { ButtonHTMLAttributes, ReactNode, forwardRef, memo } from "react";
import clsx from "clsx";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "ghost" | "danger" | "outline" | "link";
  size?: "sm" | "md" | "lg" | "icon";
  loading?: boolean;
  icon?: ReactNode;
  fullWidth?: boolean;
}

/**
 * TracQ Brand Button-Komponente (Tailwind v4)
 * - Variants: primary, secondary, ghost, danger, outline, link
 * - Sizes: sm, md, lg, icon
 * - Features: loading, icon, fullWidth
 * - TracQ Brand Colors & Typography
 * - Barrierefrei & konsistent
 */
export const Button = memo(
  forwardRef<HTMLButtonElement, ButtonProps>(
    (
      {
        children,
        variant = "primary",
        size = "md",
        loading = false,
        disabled = false,
        icon,
        fullWidth = false,
        className = "",
        ...props
      },
      ref,
    ) => {
      const variantStyles = {
        primary:
          "bg-tracq-deep-blue hover:bg-tracq-bright-blue text-white shadow-tracq-card border-0",
        secondary:
          "bg-tracq-light-orange hover:bg-tracq-deep-orange text-white shadow-tracq-card border-0",
        ghost: "bg-transparent hover:bg-tracq-deep-blue/10 text-tracq-deep-blue border-0",
        danger: "bg-destructive hover:bg-red-700 text-white shadow-card border-0",
        outline:
          "bg-transparent border-2 border-tracq-deep-blue text-tracq-deep-blue hover:bg-tracq-deep-blue hover:text-white",
        link: "bg-transparent text-tracq-deep-blue hover:text-tracq-bright-blue underline-offset-4 hover:underline border-0 shadow-none",
      };

      const sizeStyles = {
        sm: "px-3 py-1.5 text-body-sm",
        md: "px-4 py-2 text-body",
        lg: "px-6 py-3 text-subheading",
        icon: "p-2",
      };

      return (
        <button
          ref={ref}
          type="button"
          disabled={disabled || loading}
          aria-disabled={disabled || loading}
          aria-busy={loading}
          className={clsx(
            // TracQ Base Styles
            "font-poppins font-medium rounded-tracq",
            "focus-visible:ring-2 focus-visible:ring-tracq-bright-blue focus-visible:ring-offset-2",
            "transition-all duration-200 ease-in-out",
            "min-h-11 min-w-11", // Touch targets
            "inline-flex items-center justify-center",

            // Variant & Size Styles
            variantStyles[variant],
            sizeStyles[size],

            // State Styles
            fullWidth && "w-full",
            (disabled || loading) && "opacity-50 cursor-not-allowed",
            loading && "pointer-events-none",

            className,
          )}
          {...props}
        >
          {/* TracQ Spinner */}
          {loading && (
            <svg
              className="mr-2 h-4 w-4 animate-spin text-current"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          )}

          {/* Icon */}
          {icon && !loading && (
            <span className={clsx("flex-shrink-0", children && "mr-2")}>{icon}</span>
          )}

          {/* Label */}
          {children && <span className="truncate">{children}</span>}
        </button>
      );
    },
  ),
);

Button.displayName = "Button";
